'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { User, Mail, Phone, Shield, Save, KeyRound, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

export default function SettingsPage() {
  const supabase = createClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');

  const [formData, setFormData] = useState({
    fullName: '',
    artistName: '',
    email: '',
    phone: '',
    country: 'India',
  });

  // Password State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPass, setUpdatingPass] = useState(false);

  const loadUserData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      const userEmail = user.email || '';
      const fallbackName = user.user_metadata?.full_name || userEmail.split('@')[0] || 'Artist';

      setFormData({
        fullName: profile?.full_name || profile?.name || fallbackName,
        artistName: profile?.artist_name || profile?.full_name || fallbackName,
        email: userEmail,
        phone: profile?.phone || profile?.mobile || '',
        country: profile?.country || 'India',
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not logged in');

      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          full_name: formData.fullName.trim(),
          artist_name: formData.artistName.trim(),
          phone: formData.phone.trim(),
          mobile: formData.phone.trim(),
          country: formData.country,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      toast.success('Account settings saved successfully!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setUpdatingPass(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success('Password updated successfully!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update password');
    } finally {
      setUpdatingPass(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="py-24 text-center text-xs text-zinc-500">Loading settings...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6 pb-20 font-sans">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl">
          <div>
            <h1 className="text-xl font-bold text-white">Artist Settings</h1>
            <p className="text-xs text-zinc-400 mt-0.5">Manage your profile, preferences, and security.</p>
          </div>
          {activeTab === 'profile' && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 rounded-2xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-xl shadow-purple-600/30 transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              <span>Save Changes</span>
            </button>
          )}
        </div>

        {/* Layout with Sidebar Tabs */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          
          {/* Navigation Sidebar (Matching Screenshot 1) */}
          <div className="p-3 rounded-2xl bg-[#111422] border border-zinc-800 space-y-1">
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer ${
                activeTab === 'profile' ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
              }`}
            >
              <User size={15} /> Profile
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer ${
                activeTab === 'security' ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
              }`}
            >
              <Shield size={15} /> Security
            </button>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {activeTab === 'profile' && (
              <form onSubmit={handleSave} className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6 animate-in fade-in">
                <div>
                  <h2 className="text-base font-bold text-white">Account Settings</h2>
                  <p className="text-xs text-zinc-400 mt-0.5">Manage your artist credentials and contact details.</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Full Legal Name</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="Enter full legal name"
                        className="w-full px-4 py-3 pl-10 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white outline-none focus:border-purple-500"
                      />
                      <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Stage / Artist Name (Primary)</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.artistName}
                        onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                        placeholder="Primary artist name"
                        className="w-full px-4 py-3 pl-10 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white outline-none focus:border-purple-500"
                      />
                      <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-purple-400" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Registered Email (Read Only)</label>
                    <div className="relative">
                      <input
                        type="email"
                        disabled
                        value={formData.email}
                        className="w-full px-4 py-3 pl-10 rounded-xl bg-[#161a2b]/50 border border-zinc-800/80 text-xs text-zinc-500 cursor-not-allowed font-mono"
                      />
                      <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="e.g. 9356449047"
                        className="w-full px-4 py-3 pl-10 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white outline-none focus:border-purple-500"
                      />
                      <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-zinc-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-8 py-3 rounded-2xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-xl shadow-purple-600/30 transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    <span>Save Settings</span>
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'security' && (
              <div className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6 animate-in fade-in">
                <div>
                  <h2 className="text-base font-bold text-white">Change Password</h2>
                  <p className="text-xs text-zinc-400 mt-0.5">Ensure your account is using a secure password.</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className="w-full px-4 py-3 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white outline-none focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className="w-full px-4 py-3 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white outline-none focus:border-purple-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-zinc-800">
                  <button
                    type="button"
                    onClick={handleUpdatePassword}
                    disabled={updatingPass}
                    className="px-8 py-3 rounded-2xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-xl shadow-purple-600/30 transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {updatingPass ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                    <span>Update Password</span>
                  </button>
                </div>
              </div>
            )}

          </div>

        </div>

      </div>
    </AppLayout>
  );
}